import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2, FileSpreadsheet } from 'lucide-react';
import { getPostBySlug, getAllPosts } from '../../content/blog/posts';
import { 
  formatDate, 
  formatReadTime, 
  processMarkdownContent, 
  generateSEOMetadata,
  generateStructuredData,
  getRelatedPosts 
} from '../../utils/blogUtils';
import BlogCard from './BlogCard';
import BlogNavigation from './BlogNavigation';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const foundPost = getPostBySlug(slug);
    
    if (foundPost) {
      setPost(foundPost);
      
      // Get related posts
      const allPosts = getAllPosts();
      const related = getRelatedPosts(foundPost, allPosts, 3);
      setRelatedPosts(related);

      // Set SEO metadata
      const metadata = generateSEOMetadata(foundPost);
      document.title = metadata.title;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', metadata.description);
      }

      // Add structured data
      const structuredData = generateStructuredData(foundPost);
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);

      setLoading(false);

      // Cleanup function to remove structured data on unmount
      return () => {
        try {
          if (script && script.parentNode) {
            document.head.removeChild(script);
          }
        } catch (error) {
          console.warn('Script cleanup error:', error);
        }
      };
    } else {
      setLoading(false);
    }
  }, [slug]);

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // Fallback to copying URL
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      // You could add a toast notification here
      alert('Link copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/blog" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blog</span>
            </Link>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
            </div>
            
            <Link
              to="/login"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              Try Converter
            </Link>
          </div>
        </div>
      </header>

      {/* Blog Navigation */}
      <BlogNavigation />

      <div className="container mx-auto px-4 py-8">
        {/* Article Header */}
        <div className="max-w-4xl mx-auto">
          <article className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Article Meta */}
            <div className="px-8 pt-8 pb-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.publishDate)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatReadTime(post.readTime)}</span>
                  </div>
                </div>
                
                <button
                  onClick={handleShare}
                  className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Share article"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Share</span>
                </button>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {post.title}
              </h1>

              <p className="text-lg text-gray-600 mb-6">
                {post.excerpt}
              </p>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex items-center flex-wrap gap-2">
                  <Tag className="w-4 h-4 text-gray-400" />
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-6 text-sm text-gray-500">
                By {post.author || 'PDF Statement Converter Team'}
              </div>
            </div>

            {/* Article Content */}
            <div className="px-8 py-8">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: processMarkdownContent(post.content) 
                }}
              />
            </div>

            {/* Article Footer */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Was this article helpful? 
                  <Link to="/blog" className="text-indigo-600 hover:text-indigo-500 ml-2">
                    Read more articles
                  </Link>
                </div>
                
                <Link
                  to="/login"
                  className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Try Our Converter
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {relatedPosts.map((relatedPost) => (
                  <BlogCard key={relatedPost.id} post={relatedPost} />
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-12 bg-indigo-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Convert Your Bank Statements?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Put these tips into practice! Convert your SA bank statements to Sage-ready CSV format in seconds.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Try PDF Statement Converter
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">PDF Statement Converter</span>
          </div>
          <p className="text-gray-600 mb-4">Convert South African bank statements to Sage-ready CSV format</p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-gray-700">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-gray-700">
              Terms of Service
            </Link>
            <Link to="/blog" className="hover:text-gray-700">
              Blog
            </Link>
            <Link to="/" className="hover:text-gray-700">
              Back to Converter
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;